/**
 * Combine Lots — deck.gl dot-pattern overlay on the super-combo main map.
 *
 * Renders a dot-pattern fill on lots currently in `window.PsCombinedLots`
 * (the singleton selection store). Mounts ONCE on the long-lived main map
 * (`mb_map`) and reflects store changes by swapping the deck.gl layer's
 * `data` prop — the heavy resources (pattern texture, FillStyleExtension,
 * static layer props) are built once and cached on the overlay.
 *
 * Most of the deck.gl plumbing here mirrors the block-analyzer embedded
 * map (`app/frontend/lot_detail/block_analyzer/map.js`); see that file's
 * comments for the rationale behind the cold-path / hot-path split and
 * the `getCursor` no-op trick. Key differences here:
 *
 *   - The main map lives for the page lifetime, so we mount the overlay
 *     exactly once and never tear it down.
 *   - Geometry comes from the selection store's cached click-time
 *     features (phase-1 "Option A" strategy — see GH #270), not from a
 *     pre-shipped block GeoJSON.
 *   - `beforeId` is computed against the main map's style at insert
 *     time; no extra stroke layers to slot under here, just labels.
 *
 * Globals consumed:
 *   - `window.PsCombinedLots`        (selection store)
 *   - `window.PsDeckMaps`, `window.deck`   (deck.gl shim + lib)
 *   - `window.PS_DEBUG_COMBINED_LOTS`  (optional debug flag)
 *
 * Exposed as `window.PsCombinedLotsOverlay.mount(map)`. Called from
 * `super_combo.html.erb` after `mb_map.on('load')`.
 */

const OVERLAY_PROP = '_psCombinedLotsOverlay';
const SUBSCRIPTION_PROP = '_psCombinedLotsSubscription';

function debugLog(message, extra) {
  if (!(typeof window !== 'undefined' && window.PS_DEBUG_COMBINED_LOTS)) { return; }
  // eslint-disable-next-line no-console
  console.log('[combined-lots]', message, extra || '');
}

// Pick a `beforeId` so the dot pattern slots UNDER:
//   1. the purple dashed current-lot highlight strokes
//      (`lot-strokes-highlight-1` / `-highlight` / `-highlight-3` in
//      the hosted streets style), so the highlight stays visible on
//      top of the combined-lots overlay when a lot is BOTH the
//      current_lot AND in the combined list.
//   2. failing that, the topmost lot/block label layer so labels
//      stay readable on top of the pattern.
//   3. failing that, the first symbol layer in the style.
function findBeforeId(map) {
  const layers = (map.getStyle() || {}).layers || [];
  // Order matters: pick the FIRST (lowest-z) of these we find, so the
  // overlay sits below ALL of them.
  const preferred = [
    'lot-strokes-highlight-1',
    'lot-strokes-highlight',
    'lot-strokes-highlight-3',
    'lot-labels copy',
    'lot-labels',
    'block-labels',
  ];
  for (let i = 0; i < preferred.length; i++) {
    if (layers.find((layer) => layer.id === preferred[i])) {
      return preferred[i];
    }
  }
  const firstSymbol = layers.find((layer) => layer.type === 'symbol');
  return firstSymbol ? firstSymbol.id : undefined;
}

// Build (or reuse cached static state for) the deck.gl GeoJsonLayer that
// renders the dot pattern. The expensive bits — pattern canvas, GPU
// texture, FillStyleExtension instance, accessor closures — are cached
// on the overlay so subsequent calls only swap `data` + `beforeId`.
//
// Matches block-analyzer's `build_selected_lot_pattern_layer` so the two
// surfaces look identical.
function buildPatternLayer(overlay, device, data, beforeId) {
  if (window.PsDeckMaps && window.PsDeckMaps.installNoModInject) {
    window.PsDeckMaps.installNoModInject();
  }

  let cache = overlay._psPatternLayerCache;
  if (!cache || cache.device !== device) {
    const patternCanvas = window.PsDeckMaps.PATTERN_PRESETS.dense_dots();
    const texture = window.PsDeckMaps.makeMippedTexture(device, patternCanvas);
    const frameSize = texture.width;
    const tileWorldMeters = 24;

    cache = {
      device: device,
      texture: texture,
      extension: new window.deck.FillStyleExtension({ pattern: true }),
      staticProps: {
        id: 'combined_lots_pattern',
        pickable: false,
        stroked: true,
        filled: true,
        // Thin orange outline around each selected lot — matches the
        // existing dev-rights-docs orange (#FF6600) used elsewhere in
        // the app (e.g. block-analyzer overlay border). 1.5px min so
        // it stays visible at low zoom without dominating.
        lineWidthMinPixels: 1.5,
        getLineColor: [255, 102, 0, 230],
        // Orange dots (same hue as the outline) at alpha 128≈50% so
        // the pattern reads clearly on top of the lot's land-use fill.
        // Texture is a binary mask; gaps between dots stay fully
        // transparent regardless of this alpha.
        getFillColor: [255, 102, 0, 128],
        fillPatternMask: true,
        fillPatternMapping: { full: { x: 0, y: 0, width: frameSize, height: frameSize } },
        getFillPattern: function () { return 'full'; },
        getFillPatternScale: tileWorldMeters / frameSize,
        getFillPatternOffset: [0, 0],
      },
    };
    overlay._psPatternLayerCache = cache;
    debugLog('pattern layer cache initialized', { textureSize: frameSize });
  }

  return new window.deck.GeoJsonLayer(Object.assign({}, cache.staticProps, {
    data: data,
    fillPatternAtlas: cache.texture,
    extensions: [cache.extension],
    beforeId: beforeId,
  }));
}

function refreshOverlay(map) {
  const overlay = map[OVERLAY_PROP];
  if (!overlay) { return; }
  if (map._removed) { return; }
  if (!map.isStyleLoaded()) {
    debugLog('defer refresh: style not loaded');
    map.once('idle', function () { refreshOverlay(map); });
    return;
  }
  if (!(window.PsDeckMaps && window.deck && window.deck.MapboxOverlay)) {
    debugLog('skip refresh: PsDeckMaps not ready');
    return;
  }

  // Defer to next animation frame so we can grab the luma device — on
  // first mount it may not be ready yet (matches block-analyzer).
  const applyLayers = function (deviceAttempt) {
    deviceAttempt = deviceAttempt || 0;
    if (map._removed) { return; }

    const device = overlay._deck && overlay._deck.device;
    if (!device) {
      if (deviceAttempt < 30) {
        return requestAnimationFrame(function () { applyLayers(deviceAttempt + 1); });
      }
      debugLog('skip refresh: deck device not ready');
      return;
    }

    const store = window.PsCombinedLots;
    const fc = store ? store.getFeatureCollection() : { type: 'FeatureCollection', features: [] };
    const beforeId = findBeforeId(map);

    // Helper: clear the busy cursor on the next animation frame, once
    // setProps + triggerRepaint above have scheduled a render. See
    // base_map.js -> map.ps.show_busy_cursor for the rationale (and the
    // min-visible floor that prevents this from clearing too fast on
    // hot-path repeat clicks).
    const scheduleBusyCursorClear = function () {
      if (map.ps && typeof map.ps.clear_busy_cursor === 'function') {
        requestAnimationFrame(function () {
          if (map.ps && typeof map.ps.clear_busy_cursor === 'function') {
            map.ps.clear_busy_cursor();
          }
        });
      }
    };

    if (!fc.features.length) {
      if (!overlay._psFirstCallDone) {
        // Nothing to render and we've never registered the layer group
        // with Mapbox yet — skip entirely; we'll register on the first
        // non-empty toggle.
        debugLog('refresh: empty (pre-init), skipping');
        scheduleBusyCursorClear();
        return;
      }
      // Hot path: clear deck's layer set, nudge a repaint.
      overlay._deck.setProps({ layers: [] });
      map.triggerRepaint();
      debugLog('refresh: cleared');
      scheduleBusyCursorClear();
      return;
    }

    const layer = buildPatternLayer(overlay, device, fc, beforeId);

    if (!overlay._psFirstCallDone) {
      // Cold path: pay the Mapbox style-mutation tax exactly once so the
      // MapboxLayerGroup wrapper gets slotted at the correct beforeId.
      overlay.setProps({ layers: [layer] });
      overlay._psFirstCallDone = true;
      debugLog('refresh: cold-path setProps', { features: fc.features.length, beforeId: beforeId });
    } else {
      // Hot path: bypass MapboxOverlay's setProps; deck.gl re-renders
      // into the already-registered MapboxLayerGroup.
      overlay._deck.setProps({ layers: [layer] });
      map.triggerRepaint();
      debugLog('refresh: hot-path setProps', { features: fc.features.length, beforeId: beforeId });
    }

    scheduleBusyCursorClear();
  };

  requestAnimationFrame(function () { applyLayers(); });
}

/**
 * Mount the combined-lots dot-pattern overlay on the given Mapbox map.
 * Idempotent — calling more than once on the same map is a no-op.
 *
 * Wires:
 *   - `window.PsCombinedLots` subscription -> refresh overlay.
 *   - Mapbox style/source recovery on `style.load` (e.g. if user toggles
 *     map view), so the deck layer stays slotted correctly.
 */
function mount(map) {
  if (!map || !window.deck || !window.deck.MapboxOverlay) {
    debugLog('mount skipped: map or deck.gl unavailable');
    return;
  }
  if (map[OVERLAY_PROP]) {
    debugLog('mount skipped: already mounted');
    return;
  }

  const overlay = new window.deck.MapboxOverlay({
    interleaved: true,
    layers: [],
    // Returning the canvas's CURRENT cursor makes deck's per-frame cursor
    // write a no-op so it doesn't stomp on base_map.js's hover-pointer
    // cursor. See block-analyzer map.js for the full rationale.
    getCursor: function () {
      const canvas = map.getCanvas();
      return (canvas && canvas.style.cursor) || '';
    },
  });
  map[OVERLAY_PROP] = overlay;
  map.addControl(overlay);

  // Subscribe to the singleton selection store.
  const unsubscribe = window.PsCombinedLots.subscribe(function () {
    refreshOverlay(map);
  });
  map[SUBSCRIPTION_PROP] = unsubscribe;

  // If the map's style is swapped (e.g. streets <-> satellite), our
  // `_psFirstCallDone` flag is stale because the MapboxLayerGroup
  // registration lives on the old style. Reset it so the next refresh
  // re-registers via the cold path.
  map.on('style.load', function () {
    if (map[OVERLAY_PROP]) {
      map[OVERLAY_PROP]._psFirstCallDone = false;
      refreshOverlay(map);
    }
  });

  // Initial paint (e.g. if shift-clicks already accumulated before mount,
  // though in practice mount runs on map load before any clicks).
  refreshOverlay(map);

  debugLog('overlay mounted');
}

const api = { mount };

if (typeof window !== 'undefined') {
  window.PsCombinedLotsOverlay = api;
}

export default api;
